/**
 * Lights Store
 * Manages Hue lights and their states
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getDataProvider, subscribeToTable, type Subscription, type ChangePayload } from '@/lib/dataProvider'
import { useAuthStore } from './auth'
import { useHueBridgesStore } from './hueBridges'
import { hueLogger } from '@/lib/logger'
import { hueBridgeService } from '@/services/hue/HueBridgeService'
import type { HueLight, HueLightState, HueLightCommand } from '@/services/hue/HueBridgeService'
import { createDefaultWledEffectSettings } from '@/types/wledEffect'

export interface WledEffectSettings {
  effectId: number
  effectName: string
  speed: number
  intensity: number
  paletteId?: number
  gradient?: {
    stops: Array<{
      id: string
      position: number
      color: {
        hue: number
        saturation: number
        brightness: number
      }
    }>
  }
}

export interface Light {
  id: string
  user_id: string
  hue_light_id: string
  hue_bridge_id: string
  wled_device_id?: string  // WLED device reference (if this is a WLED light)
  wled_segment_id?: number  // WLED segment index this light controls
  wled_output_id?: string   // WLED output reference
  wled_effect_settings?: WledEffectSettings // WLED effect configuration
  name: string
  room_name?: string
  light_type: string
  capabilities: Record<string, unknown>
  current_state: HueLightState
  is_available: boolean
  used_in_setup: boolean
  last_seen: string
  created_at: string
  updated_at: string
  position_x?: number
  position_y?: number
}

export interface LightWithBridge extends Light {
  bridge_name?: string
  bridge_ip?: string
}

export interface LightGroup {
  room: string
  lights: LightWithBridge[]
}

export const useLightsStore = defineStore('lights', () => {
  // State
  const lights = ref<Map<string, Light>>(new Map())
  const selectedLights = ref<Set<string>>(new Set())
  
  // Realtime subscription
  let realtimeSubscription: Subscription | null = null
  
  // Loading states
  const isLoading = ref(false)
  const isUpdating = ref(false)
  
  // Error state
  const error = ref<string | null>(null)

  // Getters - access .size to track Map changes for proper reactivity
  const allLights = computed(() => {
    // Reading .size ensures Vue tracks Map mutations
    const size = lights.value.size
    return Array.from(lights.value.values())
  })
  
  const availableLights = computed(() => 
    allLights.value.filter(light => light.is_available)
  )

  const setupLights = computed(() => 
    allLights.value.filter(light => light.used_in_setup)
  )

  const lightsWithBridge = computed((): LightWithBridge[] => {
    const bridgesStore = useHueBridgesStore()
    
    return allLights.value.map(light => {
      const bridge = bridgesStore.getBridge(light.hue_bridge_id)
      return {
        ...light,
        bridge_name: bridge?.name,
        bridge_ip: bridge?.ip_address
      }
    })
  })

  const lightsByRoom = computed((): LightGroup[] => {
    const grouped = new Map<string, LightWithBridge[]>()
    
    for (const light of lightsWithBridge.value) {
      const room = light.room_name || 'Ungrouped'
      if (!grouped.has(room)) {
        grouped.set(room, [])
      }
      grouped.get(room)!.push(light)
    }
    
    return Array.from(grouped.entries()).map(([room, lights]) => ({
      room,
      lights: lights.sort((a, b) => a.name.localeCompare(b.name))
    }))
  })

  const lightsByBridge = computed(() => {
    const grouped = new Map<string, LightWithBridge[]>()
    
    for (const light of lightsWithBridge.value) {
      const bridgeId = light.hue_bridge_id
      if (!grouped.has(bridgeId)) {
        grouped.set(bridgeId, [])
      }
      grouped.get(bridgeId)!.push(light)
    }
    
    return grouped
  })

  const onlineLights = computed(() =>
    availableLights.value.filter(light => {
      const bridgesStore = useHueBridgesStore()
      const bridgeStatus = bridgesStore.bridgeStatuses.get(light.hue_bridge_id)
      return bridgeStatus?.isOnline ?? false
    })
  )

  const selectedLightsList = computed(() =>
    Array.from(selectedLights.value)
      .map(id => lights.value.get(id))
      .filter(Boolean) as Light[]
  )

  // Actions
  const setError = (message: string | null) => {
    error.value = message
  }

  const clearError = () => {
    error.value = null
  }

  /**
   * Load lights from database
   */
  const fetchLights = async (): Promise<void> => {
    const authStore = useAuthStore()
    if (!authStore.user) {
      setError('User not authenticated')
      return
    }

    try {
      isLoading.value = true
      clearError()

      const provider = getDataProvider()
      const data = await provider.query<Light>('lights', {
        orderBy: [
          { column: 'room_name', ascending: true },
          { column: 'name', ascending: true }
        ]
      })

      // Update lights map
      lights.value.clear()
      for (const light of data || []) {
        // Backfill default effect settings for WLED lights that don't have them
        if (light.wled_device_id && !light.wled_effect_settings) {
          hueLogger.info(`Backfilling default effect settings for WLED light: ${light.name}`)
          const defaultSettings = createDefaultWledEffectSettings()
          // Set immediately in local state
          light.wled_effect_settings = defaultSettings
          // Also update in DB (async, don't block)
          provider.update<Light>('lights', light.id, { 
            wled_effect_settings: defaultSettings 
          })
        }
        lights.value.set(light.id, light)
      }

    } catch (err) {
      hueLogger.error('Failed to fetch lights:', err)
      setError(err instanceof Error ? err.message : 'Failed to load lights')
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Update light state in database (triggers sync to bridge via subscription)
   */
  const updateLightState = async (lightId: string, newState: Partial<HueLightState>): Promise<void> => {
    const light = lights.value.get(lightId)
    if (!light) {
      setError('Light not found')
      return
    }

    try {
      isUpdating.value = true
      clearError()

      const updatedState = {
        ...light.current_state,
        ...newState
      }

      const provider = getDataProvider()
      const result = await provider.update('lights', lightId, {
        current_state: updatedState,
        updated_at: new Date().toISOString()
      })

      if (!result.success) {
        throw new Error(result.error)
      }

      // Update local state
      light.current_state = updatedState
      light.updated_at = new Date().toISOString()
      lights.value.set(lightId, light)

    } catch (err) {
      hueLogger.error('Failed to update light state:', err)
      setError(err instanceof Error ? err.message : 'Failed to update light')
    } finally {
      isUpdating.value = false
    }
  }

  /**
   * Update multiple lights at once
   */
  const updateMultipleLights = async (lightIds: string[], newState: Partial<HueLightState>): Promise<void> => {
    try {
      isUpdating.value = true
      clearError()

      const updates = lightIds.map(async (lightId) => {
        await updateLightState(lightId, newState)
      })

      await Promise.all(updates)
    } catch (err) {
      hueLogger.error('Failed to update multiple lights:', err)
      setError(err instanceof Error ? err.message : 'Failed to update lights')
    } finally {
      isUpdating.value = false
    }
  }

  /**
   * Control light directly via bridge (bypasses database)
   */
  const controlLightDirect = async (lightId: string, command: HueLightCommand): Promise<void> => {
    const light = lights.value.get(lightId)
    if (!light) {
      setError('Light not found')
      return
    }

    const bridgesStore = useHueBridgesStore()
    const bridge = bridgesStore.getBridge(light.hue_bridge_id)
    if (!bridge) {
      setError('Bridge not found')
      return
    }

    try {
      clearError()
      await hueBridgeService.setLightState(bridge.bridge_id, bridge.ip_address, light.hue_light_id, command)
    } catch (err) {
      hueLogger.error('Failed to control light directly:', err)
      setError(err instanceof Error ? err.message : 'Failed to control light')
    }
  }

  /**
   * Sync light from bridge data
   */
  const syncLightFromBridge = async (bridgeId: string, hueLight: HueLight): Promise<void> => {
    const authStore = useAuthStore()
    if (!authStore.user) return

    try {
      const provider = getDataProvider()
      
      // Check if light already exists
      const existingLight = Array.from(lights.value.values())
        .find(l => l.hue_bridge_id === bridgeId && l.hue_light_id === hueLight.id)

      if (existingLight) {
        // Update existing light
        const result = await provider.update('lights', existingLight.id, {
          name: hueLight.name,
          light_type: hueLight.type,
          capabilities: hueLight.capabilities,
          current_state: hueLight.state,
          is_available: hueLight.state.reachable ?? true,
          last_seen: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })

        if (result.success) {
          // Update local state
          const updated = {
            ...existingLight,
            name: hueLight.name,
            light_type: hueLight.type,
            capabilities: hueLight.capabilities,
            current_state: hueLight.state,
            is_available: hueLight.state.reachable ?? true,
            last_seen: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
          lights.value.set(existingLight.id, updated)
        }
      } else {
        // Create new light
        const result = await provider.insert<Light>('lights', {
          user_id: authStore.user.id,
          hue_light_id: hueLight.id,
          hue_bridge_id: bridgeId,
          name: hueLight.name,
          room_name: hueLight.config?.archetype ?? undefined,
          light_type: hueLight.type,
          capabilities: hueLight.capabilities,
          current_state: hueLight.state,
          is_available: hueLight.state.reachable ?? true,
          last_seen: new Date().toISOString()
        })

        if (result.success && result.data) {
          lights.value.set(result.data.id, result.data)
        }
      }
    } catch (err) {
      hueLogger.error('Failed to sync light from bridge:', err)
    }
  }

  /**
   * Update light info (name, room_name, etc.)
   */
  const updateLightInfo = async (lightId: string, updates: Partial<Pick<Light, 'name' | 'room_name'>>): Promise<void> => {
    try {
      clearError()

      const provider = getDataProvider()
      const result = await provider.update<Light>('lights', lightId, updates)

      if (!result.success) {
        throw new Error(result.error || 'Failed to update light')
      }

      if (result.data) {
        lights.value.set(result.data.id, result.data)
      }

      hueLogger.info(`Updated light ${lightId}:`, updates)
    } catch (err) {
      hueLogger.error('Failed to update light info:', err)
      setError(err instanceof Error ? err.message : 'Failed to update light')
    }
  }

  /**
   * Remove light
   */
  const removeLight = async (lightId: string): Promise<void> => {
    try {
      clearError()

      const provider = getDataProvider()
      const result = await provider.delete('lights', lightId)

      if (!result.success) {
        throw new Error(result.error)
      }

      lights.value.delete(lightId)
      selectedLights.value.delete(lightId)
    } catch (err) {
      hueLogger.error('Failed to remove light:', err)
      setError(err instanceof Error ? err.message : 'Failed to remove light')
    }
  }

  /**
   * Selection management
   */
  const selectLight = (lightId: string) => {
    selectedLights.value.add(lightId)
  }

  const deselectLight = (lightId: string) => {
    selectedLights.value.delete(lightId)
  }

  const toggleLightSelection = (lightId: string) => {
    if (selectedLights.value.has(lightId)) {
      selectedLights.value.delete(lightId)
    } else {
      selectedLights.value.add(lightId)
    }
  }

  const selectAllLights = () => {
    for (const light of allLights.value) {
      selectedLights.value.add(light.id)
    }
  }

  const deselectAllLights = () => {
    selectedLights.value.clear()
  }

  const selectLightsByRoom = (room: string) => {
    const roomLights = allLights.value.filter(light => 
      (light.room_name || 'Ungrouped') === room
    )
    for (const light of roomLights) {
      selectedLights.value.add(light.id)
    }
  }

  /**
   * Get a specific light
   */
  const getLight = (lightId: string): Light | undefined => {
    return lights.value.get(lightId)
  }

  /**
   * Get lights by bridge
   */
  const getLightsByBridge = (bridgeId: string): Light[] => {
    return allLights.value.filter(light => light.hue_bridge_id === bridgeId)
  }

  /**
   * Update WLED effect settings for a light
   */
  const updateWledEffectSettings = async (lightId: string, settings: WledEffectSettings): Promise<void> => {
    try {
      clearError()
      
      const provider = getDataProvider()
      const result = await provider.update<Light>('lights', lightId, {
        wled_effect_settings: settings
      })
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to update WLED effect settings')
      }
      
      if (result.data) {
        lights.value.set(result.data.id, result.data)
      }
    } catch (err) {
      hueLogger.error('Failed to update WLED effect settings:', err)
      setError(err instanceof Error ? err.message : 'Failed to update effect settings')
    }
  }

  /**
   * Update light position (for layout)
   */
  const updateLightPosition = async (lightId: string, x: number, y: number): Promise<void> => {
    try {
      const provider = getDataProvider()
      const result = await provider.update('lights', lightId, {
        position_x: x,
        position_y: y,
        updated_at: new Date().toISOString()
      })

      if (!result.success) {
        throw new Error(result.error)
      }

      const light = lights.value.get(lightId)
      if (light) {
        light.position_x = x
        light.position_y = y
        light.updated_at = new Date().toISOString()
        lights.value.set(lightId, light)
      }
    } catch (err) {
      hueLogger.error('Failed to update light position:', err)
      setError(err instanceof Error ? err.message : 'Failed to update light position')
    }
  }

  /**
   * Toggle light in/out of setup
   */
  const toggleLightInSetup = async (lightId: string): Promise<{ success: boolean; error?: string }> => {
    const light = lights.value.get(lightId)
    if (!light) {
      return { success: false, error: 'Light not found' }
    }

    try {
      const newSetupStatus = !light.used_in_setup

      const provider = getDataProvider()
      const result = await provider.update('lights', lightId, {
        used_in_setup: newSetupStatus,
        updated_at: new Date().toISOString()
      })

      if (!result.success) {
        throw new Error(result.error)
      }

      // Update local state
      light.used_in_setup = newSetupStatus
      light.updated_at = new Date().toISOString()
      lights.value.set(lightId, light)

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update light setup status'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Update light's current_state (for real-time UI updates during drag)
   * This does NOT persist to Hue bridge - use updateLightState for that
   * Automatically clears conflicting color mode properties to prevent race conditions
   */
  const updateCurrentState = (lightId: string, stateUpdates: Partial<HueLightState>): void => {
    const light = lights.value.get(lightId)
    if (light) {
      // Start with current state
      const newState = { ...light.current_state }
      
      // Clear conflicting color mode properties to prevent race conditions
      // If setting CT (color temperature), clear color mode properties
      if ('ct' in stateUpdates) {
        const hadColor = newState.hue != null || newState.sat != null
        delete newState.hue
        delete newState.sat
        delete newState.xy
        if (hadColor) {
          hueLogger.info(`🧹 [lights.ts] Cleared color mode from ${light.name} current_state (switching to CT)`)
        }
      }
      // If setting hue/sat (color mode), clear CT properties
      if ('hue' in stateUpdates || 'sat' in stateUpdates || 'xy' in stateUpdates) {
        const hadCT = newState.ct != null
        delete newState.ct
        if (hadCT) {
          hueLogger.info(`🧹 [lights.ts] Cleared CT from ${light.name} current_state (switching to color)`)
        }
      }
      
      // Apply updates
      Object.assign(newState, stateUpdates)
      
      // Create a new light object with updated state
      const updatedLight = {
        ...light,
        current_state: newState
      }
      
      // Create new Map to trigger reactivity
      const newLights = new Map(lights.value)
      newLights.set(lightId, updatedLight)
      lights.value = newLights
    }
  }

  /**
   * Register a WLED device as a light
   * Creates a single light entry that represents the entire LED strip as one controllable light
   */
  const registerWledLight = async (
    wledDeviceId: string,
    deviceName: string,
    ledCount: number
  ): Promise<Light | null> => {
    const authStore = useAuthStore()
    if (!authStore.user) {
      setError('User not authenticated')
      return null
    }

    try {
      // Check if light already exists for this WLED device
      const existingLight = allLights.value.find(l => l.wled_device_id === wledDeviceId)
      if (existingLight) {
        hueLogger.info(`WLED light already registered: ${existingLight.name}`)
        return existingLight
      }

      const provider = getDataProvider()
      const result = await provider.insert<Light>('lights', {
        user_id: authStore.user.id,
        hue_light_id: `wled_${wledDeviceId}`,  // Synthetic ID for WLED
        hue_bridge_id: '',  // No Hue bridge
        wled_device_id: wledDeviceId,
        name: deviceName,
        room_name: 'WLED',
        light_type: 'wled_strip',
        capabilities: {
          type: 'wled',
          ledCount: ledCount,
          rgb: true,
          cct: true,
          effects: true
        },
        current_state: {
          on: true,
          bri: 127,
          reachable: true
        } as HueLightState,
        is_available: true,
        used_in_setup: true,
        last_seen: new Date().toISOString()
      })

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to register WLED light')
      }

      lights.value.set(result.data.id, result.data)
      hueLogger.success(`Registered WLED light: ${result.data.name}`)
      return result.data
    } catch (err) {
      hueLogger.error('Failed to register WLED light:', err)
      setError(err instanceof Error ? err.message : 'Failed to register WLED light')
      return null
    }
  }

  /**
   * Register a WLED segment as a light
   * Creates a light entry that represents a specific segment of a WLED strip
   */
  const registerWledSegmentLight = async (options: {
    wledDeviceId: string
    wledOutputId: string
    segmentId: number
    name: string
    startLed: number
    ledCount: number
    isRgbw: boolean
  }): Promise<Light | null> => {
    const authStore = useAuthStore()
    if (!authStore.user) {
      setError('User not authenticated')
      return null
    }

    try {
      // Check if light already exists for this segment
      const existingLight = allLights.value.find(
        l => l.wled_device_id === options.wledDeviceId && l.wled_segment_id === options.segmentId
      )
      if (existingLight) {
        hueLogger.info(`WLED segment light already registered: ${existingLight.name}`)
        return existingLight
      }

      const provider = getDataProvider()
      const defaultEffectSettings = createDefaultWledEffectSettings()
      const result = await provider.insert<Light>('lights', {
        user_id: authStore.user.id,
        hue_light_id: `wled_${options.wledDeviceId}_seg${options.segmentId}`,
        hue_bridge_id: '',
        wled_device_id: options.wledDeviceId,
        wled_output_id: options.wledOutputId,
        wled_segment_id: options.segmentId,
        name: options.name,
        room_name: 'WLED',
        light_type: options.isRgbw ? 'wled_rgbw' : 'wled_rgb',
        capabilities: {
          type: 'wled',
          segmentId: options.segmentId,
          startLed: options.startLed,
          ledCount: options.ledCount,
          rgb: true,
          rgbw: options.isRgbw,
          cct: true,
          effects: true
        },
        current_state: {
          on: true,
          bri: 127,
          reachable: true
        } as HueLightState,
        is_available: true,
        used_in_setup: true,
        last_seen: new Date().toISOString(),
        wled_effect_settings: defaultEffectSettings
      })

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to register WLED segment light')
      }

      lights.value.set(result.data.id, result.data)
      hueLogger.success(`Registered WLED segment light: ${result.data.name} (segment ${options.segmentId})`)
      return result.data
    } catch (err) {
      hueLogger.error('Failed to register WLED segment light:', err)
      setError(err instanceof Error ? err.message : 'Failed to register WLED segment light')
      return null
    }
  }

  /**
   * Get light by WLED segment
   */
  const getLightByWledSegment = (wledDeviceId: string, segmentId: number): Light | undefined => {
    return allLights.value.find(
      light => light.wled_device_id === wledDeviceId && light.wled_segment_id === segmentId
    )
  }

  /**
   * Get lights by WLED device
   */
  const getLightsByWledDevice = (wledDeviceId: string): Light[] => {
    return allLights.value.filter(light => light.wled_device_id === wledDeviceId)
  }

  /**
   * Get all WLED lights
   */
  const wledLights = computed(() => 
    allLights.value.filter(light => !!light.wled_device_id)
  )

  /**
   * Subscribe to realtime lights updates
   */
  const subscribeToLightsUpdates = () => {
    const authStore = useAuthStore()
    if (!authStore.user) {
      hueLogger.error('No authenticated user for lights subscription')
      return null
    }

    // Unsubscribe from existing subscription
    if (realtimeSubscription) {
      realtimeSubscription.unsubscribe()
    }

    realtimeSubscription = subscribeToTable<Light>(
      'lights',
      (payload: ChangePayload<Light>) => {
        handleLightChange(payload)
      },
      { column: 'user_id', value: authStore.user.id }
    )

    hueLogger.info(`📡 Subscribed to lights updates for user: ${authStore.user.id}`)
    return realtimeSubscription
  }

  /**
   * Handle realtime light changes
   */
  const handleLightChange = (payload: ChangePayload<Light>) => {
    const { eventType, new: newRecord, old: oldRecord } = payload
    hueLogger.info('💡 Light change detected:', eventType)

    if (eventType === 'INSERT' || eventType === 'UPDATE') {
      if (newRecord) {
        lights.value.set(newRecord.id, newRecord)
        hueLogger.info(`✅ Light ${eventType.toLowerCase()}: ${newRecord.name}`)
      }
    } else if (eventType === 'DELETE') {
      if (oldRecord && oldRecord.id) {
        lights.value.delete(oldRecord.id)
        selectedLights.value.delete(oldRecord.id)
        hueLogger.info(`🗑️ Light deleted: ${oldRecord.name}`)
      }
    }
  }

  /**
   * Unsubscribe from realtime updates
   */
  const unsubscribeFromUpdates = () => {
    if (realtimeSubscription) {
      realtimeSubscription.unsubscribe()
      realtimeSubscription = null
      hueLogger.info('📡 Unsubscribed from lights updates')
    }
  }

  return {
    // State - Return raw refs for reactive Map tracking
    lights,
    selectedLights,
    isLoading: computed(() => isLoading.value),
    isUpdating: computed(() => isUpdating.value),
    error: computed(() => error.value),

    // Getters
    allLights,
    availableLights,
    setupLights,
    lightsWithBridge,
    lightsByRoom,
    lightsByBridge,
    onlineLights,
    selectedLightsList,
    wledLights,

    // Actions
    fetchLights,
    updateLightState,
    updateLightInfo,
    updateMultipleLights,
    controlLightDirect,
    syncLightFromBridge,
    removeLight,
    selectLight,
    deselectLight,
    toggleLightSelection,
    selectAllLights,
    deselectAllLights,
    selectLightsByRoom,
    getLight,
    getLightsByBridge,
    getLightsByWledDevice,
    getLightByWledSegment,
    registerWledLight,
    registerWledSegmentLight,
    toggleLightInSetup,
    updateLightPosition,
    updateWledEffectSettings,
    updateCurrentState,
    setError,
    clearError,

    // Realtime
    subscribeToLightsUpdates,
    unsubscribeFromUpdates
  }
})